"use client"

import { t } from "@/languages/i18n"
import { CaretDownOutlined, CaretUpOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Checkbox, Input, List, message, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { AttributesCategory, TagOption, TagsProps } from "../../../../lib/config/types"
import { createTag, fetchTags } from "../services/post"

const { Title, Link } = Typography

const Tags: React.FC<TagsProps> = ({ selectedTags, onChangeTags }) => {
  const [isTagsListVisible, setTagsListVisible] = useState(true)
  const [tags, setTags] = useState<TagOption[] | null>([])
  const [isAddingTag, setIsAddingTag] = useState(false)
  const [newTagName, setNewTagName] = useState("")
  const [permissions, setPermissions] = useState<Record<string, any>>({})

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedPermissions = JSON.parse(localStorage.getItem("permissions") || "{}")
      setPermissions(storedPermissions)
    }
  }, [])

  const handleFetchTag = async () => {
    try {
      const response = await fetchTags()
      const tagsData = response.data
      if (Array.isArray(tagsData)) {
        const tagOptions = tagsData.map(tag => ({
          id: tag.id,
          label: tag.attributes.name,
          value: tag.id,
          disabled: false,
        }))
        setTags(tagOptions)
      }
    } catch {
      console.error(t("Error fetching tags"))
    }
  }

  useEffect(() => {
    handleFetchTag()
  }, [])

  const handleAddTag = async () => {
    if (newTagName) {
      try {
        const slug = newTagName
          ?.normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")

        const data: AttributesCategory = {
          name: newTagName,
          slug: slug,
          description: "",
        }
        const response = await createTag(data)

        await handleFetchTag()

        setNewTagName("")
        setIsAddingTag(false)
        message.success(t(`Added new tag: ${newTagName}`))
      } catch {
        message.error(t("Error adding tag"))
      }
    } else {
      message.error(t("Please enter a tag name"))
    }
  }

  return (
    <div className="tags border border-gray-300 rounded-[10px] mb-5">
      <div className="flex justify-between items-center px-4 py-2 border-b">
        <Title level={4} className="!m-0">
          {t("Tags")}
        </Title>
        <Button
          onClick={() => setTagsListVisible(!isTagsListVisible)}
          className="flex items-center"
          icon={isTagsListVisible ? <CaretDownOutlined /> : <CaretUpOutlined />}
          type="text"
        />
      </div>

      {isTagsListVisible && (
        <div className="mb-5">
          <Checkbox.Group onChange={onChangeTags} value={selectedTags} className="w-[100%] pt-2">
            <List
              className=" w-[100%]"
              size="small"
              dataSource={tags ?? []}
              renderItem={item => (
                <List.Item>
                  <Checkbox key={item.value} value={item.value}>
                    {item.label}
                  </Checkbox>
                </List.Item>
              )}
            />
          </Checkbox.Group>
          {permissions.tag?.create.enabled && (
            <div className="px-4">
              <div className="mt-4">
                <Button
                  icon={<PlusOutlined />}
                  color="primary"
                  variant="outlined"
                  onClick={() => setIsAddingTag(!isAddingTag)}
                >
                  {t("Add New Tag")}
                </Button>
              </div>
              {isAddingTag && (
                <div className="mt-4 flex">
                  <Input
                    placeholder={t("Enter category name")}
                    value={newTagName}
                    onChange={e => setNewTagName(e.target.value)}
                    style={{ marginRight: "8px" }}
                  />
                  <Button type="primary" onClick={handleAddTag}>
                    {t("Add")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Tags
